import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { StatusBanner } from '@/components/StatusBanner';
import {
  type AdminAccount,
  type AdminRole,
  createAdminAccount,
  listAdminAccounts,
  updateAdminAccount,
} from '@/lib/adminApi';
import { useMe } from '@/lib/useMe';

const ACCOUNTS_QUERY_KEY = ['admin-accounts'] as const;
const ROLES: AdminRole[] = ['viewer', 'support', 'finance', 'superadmin'];
const MIN_REASON = 10;

/**
 * Who may use this console, and with which role (ClickUp 869f0ev3c access
 * model: one superadmin, everyone else read-only).
 *
 * Any role sees the list. Only a superadmin gets the add form, the role
 * selector and the deactivate button — and never on their own row (the API
 * refuses a self-demotion and the last-superadmin removal with 409 either
 * way; this page just does not offer them). Every change needs a reason,
 * which the API writes into admin_audit_log together with before/after.
 */
export function AdminAccountsPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const isSuperadmin = me?.role === 'superadmin';

  const { data, isLoading, error } = useQuery({
    queryKey: ACCOUNTS_QUERY_KEY,
    queryFn: listAdminAccounts,
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('viewer');
  const [addReason, setAddReason] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [banner, setBanner] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });

  const create = useMutation({
    mutationFn: createAdminAccount,
    onSuccess: (row) => {
      setBanner({ kind: 'success', message: `Added ${row.email} as ${row.role}.` });
      setEmail('');
      setAddReason('');
      void refresh();
    },
    onError: (err: Error) => setBanner({ kind: 'error', message: err.message }),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: { role?: AdminRole; active?: boolean; reason: string } }) =>
      updateAdminAccount(id, input),
    onSuccess: (row) => {
      setBanner({ kind: 'success', message: `Updated ${row.email}.` });
      void refresh();
    },
    onError: (err: Error) => setBanner({ kind: 'error', message: err.message }),
  });

  function requireChangeReason(): string | null {
    const reason = changeReason.trim();
    if (reason.length < MIN_REASON) {
      setBanner({ kind: 'error', message: `Enter a reason of at least ${MIN_REASON} characters before changing an account.` });
      return null;
    }
    return reason;
  }

  function changeRole(account: AdminAccount, next: AdminRole) {
    if (next === account.role) return;
    const reason = requireChangeReason();
    if (!reason) return;
    update.mutate({ id: account.id, input: { role: next, reason } });
  }

  function deactivate(account: AdminAccount) {
    const reason = requireChangeReason();
    if (!reason) return;
    update.mutate({ id: account.id, input: { active: false, reason } });
  }

  function submitAdd(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = addReason.trim();
    if (reason.length < MIN_REASON) {
      setBanner({ kind: 'error', message: `Enter a reason of at least ${MIN_REASON} characters.` });
      return;
    }
    create.mutate({ email: email.trim(), role, reason });
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-h2 font-semibold text-fg-primary">Admin accounts</h1>
        <p className="text-meta text-fg-secondary">
          {isSuperadmin
            ? 'Who may use this console. Every change is recorded in the audit log with its reason.'
            : 'Who may use this console. Your role is read-only here.'}
        </p>
      </div>

      {banner && <StatusBanner kind={banner.kind} message={banner.message} />}

      {isSuperadmin && (
        <form
          data-testid="add-admin-account"
          onSubmit={submitAdd}
          className="rounded-card border border-border bg-surface-field p-4"
        >
          <div className="mt-1 text-h3 font-semibold text-fg-primary">Add an account</div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Email
              <input
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Role
              <select
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
              Reason
              <input
                name="reason"
                value={addReason}
                onChange={(e) => setAddReason(e.target.value)}
                className="rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary"
              />
            </label>
            <button
              type="submit"
              disabled={create.isPending}
              className="self-start rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </form>
      )}

      {isSuperadmin && (
        <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
          Reason for a role change or deactivation
          <input
            name="change-reason"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
          />
        </label>
      )}

      {isLoading && <p className="text-meta text-fg-secondary">Loading…</p>}
      {error && <StatusBanner kind="error" message={(error as Error).message} />}

      {data && (
        <table className="w-full text-left text-body">
          <thead>
            <tr>
              <th className="py-2 pr-4 font-medium">Email</th>
              <th className="py-2 pr-4 font-medium">Role</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {data.accounts.map((account) => {
              const own = account.email.toLowerCase() === me?.email.toLowerCase();
              const canEdit = isSuperadmin && !own && account.active;
              return (
                <tr key={account.id} data-testid="admin-account-row">
                  <td className="py-2 pr-4 text-fg-primary">{account.email}</td>
                  <td className="py-2 pr-4 text-fg-secondary">
                    {canEdit ? (
                      <select
                        data-testid="role-select"
                        value={account.role}
                        disabled={update.isPending}
                        onChange={(e) => changeRole(account, e.target.value as AdminRole)}
                        className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      account.role
                    )}
                  </td>
                  <td className="py-2 pr-4 text-fg-secondary">{account.active ? 'active' : 'deactivated'}</td>
                  <td className="py-2">
                    {canEdit && (
                      <button
                        type="button"
                        data-testid="deactivate"
                        disabled={update.isPending}
                        onClick={() => deactivate(account)}
                        className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
